import type {
  DivisionLeaderboardSummaryPublic,
  DivisionPublic,
  LeagueEpisodeThumbnail,
  LeaguePublic,
} from "@softmax/observatory-client/types";

import { loadWatchLeagueState } from "@/lib/leagues/watch";
import { listedPublic } from "@/lib/leagues/browse-hidden";
import { listedGamePage } from "@/data/catalog";
import { publicRead } from "@/data/api";
import { listedBrowseRows } from "./catalog/browse-rows";

export async function browseCatalog(
  cursor: string | null,
  signal?: AbortSignal,
) {
  const page = await listedGamePage({ cursor, signal });
  return {
    rows: listedBrowseRows(listedPublic(page.leagues)),
    nextCursor: page.next_cursor,
  };
}

export async function browseLeaderboard(
  leagueId: string,
  signal?: AbortSignal,
) {
  const api = {
    get: <T>(path: string) => publicRead<T>({ path, signal }),
  };
  const divisions = await api.get<DivisionPublic[]>(
    `/v2/divisions?${new URLSearchParams({ league_id: leagueId, type: "competition" })}`,
  );

  const [firstDivision] = divisions;

  if (!firstDivision) return null;

  let divisionId: string | null = firstDivision.id;

  if (divisions.length > 1) {
    const league = await api.get<LeaguePublic>(
      `/v2/leagues/${encodeURIComponent(leagueId)}`,
    );
    divisionId = (await loadWatchLeagueState({ api, league, wave: "feature" }))
      .divisionId;
  }

  if (divisionId === null) return null;

  const board = await api.get<DivisionLeaderboardSummaryPublic | null>(
    `/v2/divisions/${encodeURIComponent(divisionId)}/leaderboard/summary`,
  );
  return (
    board && {
      players: board.entries.length,
      policies: new Set(
        board.entries.flatMap((entry) =>
          entry.policy_label ? [entry.policy_label] : [],
        ),
      ).size,
    }
  );
}

export function browseThumbnails(leagueIds: string[], signal?: AbortSignal) {
  const parameters = new URLSearchParams();

  for (const leagueId of leagueIds) parameters.append("league_id", leagueId);

  return publicRead<LeagueEpisodeThumbnail[]>({
    path: `/v2/leagues/episode-thumbnails?${parameters}`,
    signal,
  });
}
